import { FC, memo, useRef, useState } from 'react';
import { useStyles } from './ActionButtons.styles';
import { EvaluationStatus } from '@modules/EvaluationModule/enums/EvaluationStatus';
import { RockyButton } from '@shared/components/RockyButton';
import { paths } from '@shared/enums/paths';
import { StartEvalModal } from '../StartEvalModal/StartEvalModal';
import { MyTeamEvalData } from '@modules/EvaluationModule/interfaces/MyTeamEvalData';
import { useModalState } from '@shared/hooks/useModalState';
import { ScheduleEvalMetting } from '../../../ScheduleEvalMetting/ScheduleEvalMetting';
import { StartEvalData } from '@modules/EvaluationModule/interfaces/StartEvalData';
import { FailEopModal } from '../FailEopModal/FailEopModal';
import { PassEopModal } from '../PassEopModal/PassEopModal';
import { EvaluationType } from '@modules/EvaluationModule/enums/EvaluationType';
import { EopResult } from '@modules/EvaluationModule/enums/EopResult';
import { ReactComponent as ArrowDown } from '@assets/icons/ArrowDown.svg';
import { useClickOutside } from '@shared/hooks/useClickOutside';
import { RescheduleModal } from '../RescheduleModal/RescheduleModal';
import { Stack } from '@mui/material';
import { getEvaluationTerms } from '@shared/helpers/evaluationTerminology';
interface Props {
  evaluation: MyTeamEvalData;
  handleSendTheFormButtonClick: (startEvalData: StartEvalData) => void;
  handleFailEopButtonClick: (evaluationId: string, failReason: string) => void;
  handlePassEopButtonClick: (evaluationId: string) => void;
  officeCountry?: string;
}

const ActionButtonsComponent: FC<Props> = ({
  evaluation,
  handleSendTheFormButtonClick,
  handleFailEopButtonClick,
  handlePassEopButtonClick,
  officeCountry,
}) => {
  const { openModal, closeModal, isModalOpen } = useModalState();
  const { openModal: openExtendModal, closeModal: closeExtendModal, isModalOpen: isExtendModalOpen } = useModalState();
  const { openModal: openEopModal, closeModal: closeEopModal, isModalOpen: isEopModalOpen } = useModalState();
  const {
    openModal: openPassEopModal,
    closeModal: closePassEopModal,
    isModalOpen: isPassEopModalOpen,
  } = useModalState();
  const styles = useStyles();
  const [isDropdownOpen, setDropdownIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  useClickOutside(wrapperRef, () => {
    setDropdownIsOpen(false);
  });

  const {
    name,
    email,
    userId,
    status,
    evaluationId,
    isSubmittedByEvaluator,
    isSubmittedByEvaluatee,
    primaryReportsTo,
    typeOfInteraction,
    evaluationDate,
    isExtendable,
  } = evaluation;

  const isEOPExtensionDisabled = !isExtendable;
  const isEop = typeOfInteraction === EvaluationType.EndOfProbation;
  const isFinalizedEop =
    isEop &&
    status === EvaluationStatus.Closed &&
    (evaluation.eopStatus === EopResult.Extended || evaluation.eopStatus === EopResult.NotPassed);
  const isScheduleBtnShowen = isSubmittedByEvaluatee && isSubmittedByEvaluator && status === EvaluationStatus.Ongoing;
  const isFillInFormButtonShown = status === EvaluationStatus.Ongoing;

  const dropdownDisplayCondition = isFinalizedEop
    ? false
    : isEop
    ? status === EvaluationStatus.Upcoming && !evaluation.eopStatus
    : status === EvaluationStatus.Ongoing || status === EvaluationStatus.Upcoming;

  const isStartEvalButtonShown =
    !isFinalizedEop && status === EvaluationStatus.Upcoming && (!isEop || evaluation.eopStatus === EopResult.Passed);

  const isSetAsPassedButtonShown =
    !isFinalizedEop && isEop && status === EvaluationStatus.Upcoming && evaluation.eopStatus !== EopResult.Passed;

  return (
    <div className={styles.buttonsBlock} onClick={(e) => e.stopPropagation()} ref={wrapperRef}>
      <ScheduleEvalMetting
        googleCalendarEvalEventsMeetingLinkData={{
          evaluateeName: name,
          evaluateeEmail: email,
          userId,
          evaluationId,
          evaluatorEmail: primaryReportsTo.email,
        }}
        googleCalendarEvent={evaluation.googleCalendarEvent}
        isScheduleBtnShowen={isScheduleBtnShowen}
        isLoading={evaluation === null}
      />
      <div>
        <Stack direction="row">
          {isFillInFormButtonShown ? (
            <RockyButton
              small
              action
              className={dropdownDisplayCondition ? styles.button : styles.setResolutionButton}
              to={`${paths.users}/${userId}/${paths.evaluations}/${evaluationId}`}
            >
              Provide feedback
            </RockyButton>
          ) : isStartEvalButtonShown ? (
            <RockyButton
              className={dropdownDisplayCondition ? styles.button : styles.setResolutionButton}
              action
              small
              onClick={() => openModal()}
            >
              Start evaluation
            </RockyButton>
          ) : isSetAsPassedButtonShown ? (
            <RockyButton
              className={dropdownDisplayCondition ? styles.button : styles.setResolutionButton}
              action
              small
              to={`${paths.users}/${userId}/${paths.evaluations}/${evaluationId}/${paths.endOfProbation}`}
            >
              Set resolution
            </RockyButton>
          ) : null}
          {dropdownDisplayCondition && (
            <RockyButton className={styles.extendBtn} action small onClick={() => setDropdownIsOpen(true)}>
              <ArrowDown />
            </RockyButton>
          )}
        </Stack>
        {isDropdownOpen && (
          <div className={styles.extendList}>
            <span className={styles.listTitle}>
              {isEop ? getEvaluationTerms(officeCountry).endOfPeriod : 'Evaluation'}
            </span>
            {isEop ? (
              <>
                <RockyButton className={styles.listButton} small onClick={openPassEopModal}>
                  Set as passed 👍
                </RockyButton>
                <RockyButton
                  className={styles.listButton}
                  small
                  onClick={openExtendModal}
                  disabled={isEOPExtensionDisabled}
                >
                  {getEvaluationTerms(officeCountry).extendPeriod}
                </RockyButton>
                <RockyButton className={styles.listButton} small onClick={openEopModal}>
                  Set as failed 🚷
                </RockyButton>
              </>
            ) : (
              <RockyButton className={styles.listButton} small onClick={openExtendModal}>
                Reschedule
              </RockyButton>
            )}
          </div>
        )}
        <FailEopModal
          evaluationId={evaluationId}
          closeEopModal={closeEopModal}
          isEopModalOpen={isEopModalOpen}
          handleSubmit={handleFailEopButtonClick}
          officeCountry={officeCountry}
        />
        <PassEopModal
          evaluationId={evaluationId}
          closeModal={closePassEopModal}
          isModalOpen={isPassEopModalOpen}
          handleSubmit={handlePassEopButtonClick}
          officeCountry={officeCountry}
          userName={name}
        />
        <RescheduleModal
          isEop={isEop}
          evaluationData={{
            evaluationDate,
            evaluationId,
            evaluateeName: name,
            peoplePartnerName: null,
            status: status as EvaluationStatus,
          }}
          isModalOpen={isExtendModalOpen}
          closeModal={closeExtendModal}
          officeCountry={officeCountry}
        />
        {isModalOpen && (
          <StartEvalModal
            evaluation={evaluation}
            handleSubmit={handleSendTheFormButtonClick}
            isModalOpen={isModalOpen}
            closeModal={closeModal}
          />
        )}
      </div>
    </div>
  );
};

export const ActionButtons = memo(ActionButtonsComponent);
